import { saveAs } from "file-saver";

const FILE_TYPES = {
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    xls: "application/vnd.ms-excel",
    csv: "text/csv",
    zip: "application/zip",
};

/**
 * Determine the MIME type from a suggested file name.
 * Falls back to "application/octet-stream" for unknown extensions.
 */
const getMimeType = (suggestedName) => {
    const ext = (suggestedName.split(".").pop() || "").toLowerCase();
    return FILE_TYPES[ext] || "application/octet-stream";
};

/**
 * Build the `type` array expected by showSaveFilePicker.
 */
const buildAcceptTypes = (suggestedName, mimeType) => {
    const ext = (suggestedName.split(".").pop() || "").toLowerCase();
    return {
        [mimeType]: [`.${ext}`],
    };
};

/**
 * Sanitize a suggested file name so it does not contain characters
 * that are invalid in most file systems.
 */
const sanitizeFileName = (name) => {
    return (name || "download")
        .replace(/[\\/:*?"<>|]/g, "_")
        .trim() || "download";
};

/**
 * Save a Blob (or Promise<Blob>) to the user's disk.
 *
 * On Chromium-based browsers (Chrome, Edge) that support the File System
 * Access API, this opens a native "Save As" dialog where the user can
 * choose the destination folder and rename the file before it is written.
 * This function resolves `true` ONLY after the file has been fully written
 * to the user-selected destination, so callers can safely show a
 * "downloaded successfully" message based on the returned value.
 *
 * IMPORTANT: To ensure the Save As dialog appears reliably, this function
 * opens the file picker SYNCHRONOUSLY (within the user's click event) before
 * awaiting the blob. This avoids the "transient user activation" timeout that
 * occurs when the blob takes time to fetch/generate.
 *
 * On browsers without showSaveFilePicker (Firefox, Safari), it falls back
 * to `saveAs` from file-saver, which triggers the browser's default download
 * (and download manager) using the suggested file name.
 *
 * @param {Blob | Promise<Blob>} blobOrPromise - The file content to save, or a promise that resolves to the blob.
 * @param {string} suggestedName - The default file name (e.g. "Project_Cost_Summary.xlsx").
 * @param {string} [mimeType] - Optional MIME type override. Auto-detected from the extension if omitted.
 * @returns {Promise<boolean>} Resolves `true` ONLY after the file was really
 *   saved to disk (picker write completed) — or, on browsers without the
 *   picker, after the fallback download was successfully initiated.
 *   Resolves `false` ONLY when the user cancelled the Save As dialog.
 *   THROWS on any other failure (e.g. blob generation/fetch failed or the
 *   write to the picked file failed), so callers can show an error instead
 *   of a false success message.
 */
export const saveFileWithPrompt = async (blobOrPromise, suggestedName, mimeType) => {
    const fileName = sanitizeFileName(suggestedName);
    const mime = mimeType || getMimeType(fileName);

    // Attach a safety catch so a blob promise that rejects AFTER the user has
    // already cancelled the dialog (or after the fallback download was
    // initiated) does not become an unhandled promise rejection. The original
    // promise still rejects for normal callers, so genuine errors are still
    // caught by the caller's try/catch.
    if (blobOrPromise && typeof blobOrPromise.then === "function") {
        blobOrPromise.catch(() => {});
    }

    if (window.showSaveFilePicker) {
        try {
            // Open the picker FIRST (synchronously, within user activation window)
            const handle = await window.showSaveFilePicker({
                suggestedName: fileName,
                types: [{
                    description: `${fileName.split(".").pop().toUpperCase()} File`,
                    accept: buildAcceptTypes(fileName, mime),
                }],
            });

            // NOW await the blob (fetch/zip can happen while dialog is open)
            const blob = await Promise.resolve(blobOrPromise);

            // Write the file to the user-selected destination.
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();

            // The file was really written — this is the ONLY point in the
            // picker path where we report success.
            return true;
        } catch (err) {
            // The user cancelled the dialog (AbortError) — the download did not
            // happen, but it is not an error either. Report "not saved" so the
            // caller shows no message at all.
            if (err.name === "AbortError") {
                return false;
            }
            // Any OTHER failure (blob generation/fetch failed, write to the
            // picked file failed, picker unavailable mid-flow, etc.) means the
            // file was NOT successfully downloaded. Rethrow so callers never
            // show a success message for an unsaved download — they will show
            // their "Failed to download" error instead.
            console.warn("saveFileWithPrompt: file was not saved, rethrowing failure:", err);
            throw err;
        }
    }

    // Fallback for browsers without showSaveFilePicker (Firefox, Safari).
    // Here we cannot verify the OS-level write completed, but we DO wait for
    // the blob promise to resolve (proving the backend generation + fetch
    // succeeded) before reporting success.
    const blob = await Promise.resolve(blobOrPromise);

    // Validate that we actually received a usable Blob. Calling saveAs with
    // an invalid payload can fail silently in some browsers, which would
    // otherwise produce a false "downloaded successfully" message.
    if (!(blob instanceof Blob)) {
        throw new Error("Download failed: the generated file is not a valid Blob.");
    }

    try {
        saveAs(blob, fileName);
    } catch (err) {
        // If saveAs itself throws, the download was NOT handed off to the
        // browser, so rethrow a clear error. Callers can then show a
        // "Failed to download" message instead of a false success.
        console.error("saveFileWithPrompt: saveAs threw an error:", err);
        throw new Error(`Download failed: ${err?.message || "unable to initiate the download in this browser"}`);
    }

    return true;
};

/**
 * Fetch a URL, convert it to a Blob, then save it with the Save As prompt.
 *
 * Useful for backend-generated files (e.g. excel_url) so that the user sees
 * a friendly, descriptive name instead of the random UUID name in the URL.
 *
 * IMPORTANT: This function passes a Promise<Blob> to saveFileWithPrompt so
 * the Save As dialog opens immediately (within the user's click activation)
 * while the fetch happens in the background. This ensures the dialog appears
 * reliably regardless of network latency.
 *
 * @param {string} url - The URL of the file to download.
 * @param {string} suggestedName - The default file name for the Save As dialog.
 * @returns {Promise<boolean>} Resolves `true` if the file was really saved
 *   (picker write completed) OR the fallback download was successfully
 *   initiated. Resolves `false` ONLY when the user cancelled the Save As dialog.
 */
export const downloadUrlWithPrompt = async (url, suggestedName) => {
    // Create a blob promise without awaiting it yet
    const blobPromise = fetch(url).then(response => {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status} while fetching ${url}`);
        }
        return response.blob();
    });

    // Pass the promise to saveFileWithPrompt — the picker opens first,
    // then the fetch completes while the dialog is displayed
    return saveFileWithPrompt(blobPromise, suggestedName);
};
